// v10/src/utils/idbQueue.js
//
// Utilidad de IndexedDB para la cola offline de inspecciones.
// A diferencia de localStorage, IndexedDB es accesible desde
// el Service Worker, lo que permite el Background Sync real.
//
// Usado por:
//   - useOfflineQueue.js (app React)
//   - service-worker.js (background sync)

const DB_NAME    = 'condor360_db';
const DB_VERSION = 1;
const STORE_QUEUE  = 'offline_queue';
const STORE_CONFIG = 'config'; // para guardar el token y API URL

// ── Abrir / inicializar la DB ─────────────────────────────────────
export const openDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, DB_VERSION);

  req.onupgradeneeded = (e) => {
    const db = e.target.result;

    // Store de cola de inspecciones
    if (!db.objectStoreNames.contains(STORE_QUEUE)) {
      const store = db.createObjectStore(STORE_QUEUE, { keyPath: 'id' });
      store.createIndex('status',    'status',    { unique: false });
      store.createIndex('createdAt', 'createdAt', { unique: false });
    }

    // Store de configuración (token, apiUrl)
    if (!db.objectStoreNames.contains(STORE_CONFIG)) {
      db.createObjectStore(STORE_CONFIG, { keyPath: 'key' });
    }
  };

  req.onsuccess = (e) => resolve(e.target.result);
  req.onerror   = (e) => reject(e.target.error);
});

// ── Cola de inspecciones ──────────────────────────────────────────

export const enqueueInspection = async (payload) => {
  const db    = await openDB();
  const entry = {
    id:         `oq_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    payload,
    createdAt:  new Date().toISOString(),
    retryCount: 0,
    status:     'pending', // pending | synced | failed
    lastError:  null,
  };
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_QUEUE, 'readwrite');
    const store = tx.objectStore(STORE_QUEUE);
    const req   = store.add(entry);
    req.onsuccess = () => resolve(entry);
    req.onerror   = (e) => reject(e.target.error);
  });
};

export const getPendingInspections = async () => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx      = db.transaction(STORE_QUEUE, 'readonly');
    const store   = tx.objectStore(STORE_QUEUE);
    const index   = store.index('status');
    const req     = index.getAll('pending');
    req.onsuccess = (e) => resolve(e.target.result || []);
    req.onerror   = (e) => reject(e.target.error);
  });
};

export const updateQueueEntry = async (id, updates) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_QUEUE, 'readwrite');
    const store = tx.objectStore(STORE_QUEUE);
    const getReq = store.get(id);
    getReq.onsuccess = (e) => {
      const entry   = e.target.result;
      if (!entry) return resolve(null);
      const updated = { ...entry, ...updates };
      const putReq  = store.put(updated);
      putReq.onsuccess = () => resolve(updated);
      putReq.onerror   = (err) => reject(err.target.error);
    };
    getReq.onerror = (e) => reject(e.target.error);
  });
};

export const getPendingCount = async () => {
  const pending = await getPendingInspections();
  return pending.length;
};

// ── Config (token + apiUrl para el SW) ───────────────────────────

export const setConfig = async (key, value) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_CONFIG, 'readwrite');
    const store = tx.objectStore(STORE_CONFIG);
    const req   = store.put({ key, value });
    req.onsuccess = () => resolve();
    req.onerror   = (e) => reject(e.target.error);
  });
};

export const getConfig = async (key) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_CONFIG, 'readonly');
    const store = tx.objectStore(STORE_CONFIG);
    const req   = store.get(key);
    req.onsuccess = (e) => resolve(e.target.result?.value ?? null);
    req.onerror   = (e) => reject(e.target.error);
  });
};
